import type { FolderForDialog } from "@types"

/** Clé du nœud racine « Mon espace » dans le TreeTable. */
export const WORKSPACE_ROOT_TREE_KEY = "__workspace_root__"

/** Nœud tableau arborescent (PrimeVue TreeTable / BaseTableTree). */
export type FolderMoveTreeNode = {
    key: string
    data: {
        _id_folder: string | null
        folder: string
        building: number
    }
    children: FolderMoveTreeNode[]
}

/**
 * Normalise un identifiant dossier API (chaîne vide / undefined → null).
 */
export function normalizeFolderId(
    id: string | null | undefined
): string | null {
    if (id === undefined || id === null || id === "") return null
    return id
}

/**
 * Collecte récursivement tous les ids du sous-arbre dont la racine est `rootId`.
 */
export function collectSubtreeIds(
    nodes: FolderForDialog[],
    rootId: string
): Set<string> | null {
    for (const n of nodes) {
        if (n._id === rootId) {
            const out = new Set<string>()
            function walk(x: FolderForDialog) {
                out.add(x._id)
                for (const c of x.children ?? []) walk(c)
            }
            walk(n)
            return out
        }
        const sub = collectSubtreeIds(n.children ?? [], rootId)
        if (sub) return sub
    }
    return null
}

/**
 * Retire de l’arbre tout nœud dont l’id est dans `exclude` (y compris descendants exclus avec le parent).
 */
export function filterFolderTreeByIds(
    nodes: FolderForDialog[],
    exclude: Set<string>
): FolderForDialog[] {
    const out: FolderForDialog[] = []
    for (const n of nodes) {
        if (exclude.has(n._id)) continue
        out.push({
            ...n,
            children: filterFolderTreeByIds(n.children ?? [], exclude),
        })
    }
    return out
}

/**
 * Transforme l’API dossiers (`FolderForDialog`) en nœuds compatibles `BaseTableTree` (champ `data`).
 */
export function folderForDialogToTableNodes(
    folderTree: FolderForDialog[]
): FolderMoveTreeNode[] {
    return folderTree.map((folder) => ({
        key: folder._id,
        data: {
            _id_folder: folder._id,
            folder: folder.name,
            building: Array.isArray(folder.buildings)
                ? folder.buildings.length
                : 0,
        },
        children:
            folder.children && folder.children.length > 0
                ? folderForDialogToTableNodes(folder.children)
                : [],
    }))
}

/**
 * Enveloppe l’arbre sous « Mon espace » (parent `null` API).
 */
export function wrapFolderTreeWithWorkspaceRoot(
    folderTree: FolderForDialog[]
): FolderMoveTreeNode[] {
    const children = folderForDialogToTableNodes(folderTree)
    return [
        {
            key: WORKSPACE_ROOT_TREE_KEY,
            data: {
                _id_folder: null,
                folder: "Mon espace",
                building: 0,
            },
            children,
        },
    ]
}
